import { gql, useQuery, useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import FormControl from "react-bootstrap/FormControl";
import ToggleButton from "react-bootstrap/ToggleButton";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  GetMeasurementStationEditDetails,
  GetMeasurementStationEditDetails_station,
  GetMeasurementStationEditDetailsVariables,
} from "./__generated__/GetMeasurementStationEditDetails";
import { GetMapLayersQuery } from "utils/__generated__/GetMapLayersQuery";
import { GET_MAP_LAYERS } from "utils/stations";
import {
  GetStationSources,
  GetStationSources_datasource,
  GetStationSourcesVariables,
} from "./__generated__/GetStationSources";
import {
  GetOrganizations,
  GetOrganizations_organization,
} from "../utils/__generated__/GetOrganizations";
import { gettext } from "../i18n";
import { GET_ORGANIZATIONS } from "../utils/users";
import {
  GET_MEASUREMENT_STATIONS,
  GET_DASHBOARD_STATIONS,
} from "utils/stations";
import { isValidDataPointValue } from "utils/datacharts";
import apiClient from "../api";
import BackToLink from "../components/common/BackToLink";
import {
  TILT_AXIS_LAYOUTS,
  TILT_AXIS_META,
  getTiltAxisLayout,
  getEffectiveTiltAngles,
} from "../components/map/MapLegend";
import FormattedFormControl from "components/form/FormattedFormControl";
import SessionTimeout from "components/common/SessionTimeout";
import {
  timestampToDatetimeString,
  dateToString,
  stringToDate,
} from "utils/formatting";
import { sortByTranslatedKey } from "utils/arrays";
import { useAuth } from "../state/auth";

const GET_STATION_SOURCES = gql`
  query GetStationSources($stationId: Int) {
    datasource(where: { station_id: { _eq: $stationId } }) {
      name
      source_uuid
      source_id
      type
      type
      unit {
        id
        symbol
      }
    }
  }
`;

const GET_EDIT_DETAILS = gql`
  query GetMeasurementStationEditDetails($identifier: String) {
    station(where: { identifier: { _eq: $identifier } }) {
      id
      identifier
      name
      lat
      lon
      reference_lat
      reference_lon
      organization_id
      organization {
        slug
      }
      map_layer
      status
      station_type
      primary_source_uuid
      distance_alert_threshold
      alert_recipient_emails
      ftp_config_file
      ftp_config_transmitted_at
      ftp_firmware_file
      ftp_firmware_transmitted_at
      ftp_firmware_version
      config {
        key
        value
        display_name
      }
    }
  }
`;

const ALLOWED_CONFIG_KEYS = [
  "hbdelay",
  "v24v_pup",
  "v12v_pup",
  "v5v_pup",
  "pnum1",
  "pnum2",
];

const CONFIG_DEFAULT_LABELS: Record<string, string> = {
  hbdelay: "Hbdelay",
  v24v_pup: "v24vPup",
  v12v_pup: "v12vPup",
  v5v_pup: "v5vPup",
  pnum1: "pnum1",
  pnum2: "pnum2",
};

const MeasurementStationEditPage = () => {
  const { identifier } = useParams();
  const { user } = useAuth();
  const { data, error, refetch } = useQuery<
    GetMeasurementStationEditDetails,
    GetMeasurementStationEditDetailsVariables
  >(GET_EDIT_DETAILS, {
    variables: {
      identifier,
    },
  });
  const [editedValues, setEditedValues] = React.useState<Record<string, any>>(
    {},
  );
  const [configDisplayNamesEdited, setConfigDisplayNamesEdited] =
    React.useState<boolean>(false);
  const [configDisplayNames, setConfigDisplayNames] = React.useState<
    Record<string, string>
  >({});
  const setValueHandler =
    (key: string, parser?: (raw: string) => any) => (e: any) => {
      let value = parser ? parser(e.target.value) : e.target.value;
      setValue(key, value);
    };
  const setValue = (key: string, value: any) => {
    setEditedValues({
      ...editedValues,
      [key]: value,
    });
  };
  const setDisplayName = (key: string, displayName: string) => {
    setConfigDisplayNamesEdited(true);
    setConfigDisplayNames({
      ...configDisplayNames,
      [key]: displayName,
    });
  };
  const setDisplayNameHandler = (key: string) => (e: any) => {
    setDisplayName(key, e.target.value);
  };
  const apolloClient = useApolloClient();
  const defaultInterval = 900;

  const station = data?.station?.[0];
  const config: Record<string, any> = {};
  for (const keyValuePair of station?.config ?? []) {
    config[keyValuePair.key] = keyValuePair.value;
  }

  React.useEffect(() => {
    if (!station?.config) {
      return;
    }

    const initialDisplayNames: Record<string, string> = {};
    for (const keyValuePair of station?.config ?? []) {
      initialDisplayNames[keyValuePair.key] = keyValuePair.display_name;
    }

    setConfigDisplayNames(initialDisplayNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station]);

  if (!data) {
    return <div />;
  }
  if (error) {
    console.error(JSON.stringify(error, null, 2));
    return <SessionTimeout />;
  }
  if (!station || data?.station.length === 0) {
    return <div>Not found.</div>;
  }

  const submitDisabled =
    Object.keys(editedValues).length === 0 && !configDisplayNamesEdited;

  const onSave = (e: any) => {
    e.preventDefault();
    if (submitDisabled) {
      return;
    }
    if ((editedValues.station_type ?? station.station_type) === "tiltmeter") {
      // Read from station.config: the local `config` below is block-scoped
      // and shadows the component-level one until its declaration.
      const savedTiltAxisAngles = station.config?.find(
        (configPair) => configPair.key === "tilt_axis_angles",
      )?.value as Record<string, number> | undefined;
      const savedTiltAxisLayout = station.config?.find(
        (configPair) => configPair.key === "tilt_axis_layout",
      )?.value as string | undefined;
      const tiltAxisLayout = getTiltAxisLayout(
        editedValues.tilt_axis_layout ?? savedTiltAxisLayout,
      );
      const mergedTiltAxisAngles = getEffectiveTiltAngles(tiltAxisLayout.id, {
        ...(savedTiltAxisAngles ?? {}),
        ...(editedValues.tilt_axis_angles ?? {}),
      });
      const distinctAngles = new Set(Object.values(mergedTiltAxisAngles));
      if (distinctAngles.size < tiltAxisLayout.directions.length) {
        toast.error(
          gettext("Each tilt axis must point in a different direction"),
        );
        return;
      }
    }
    const config: Record<string, any> = {};
    const values = { ...editedValues };
    if (values.measurementInterval) {
      config.measurement_interval = values.measurementInterval;
      delete values.measurementInterval;
    }
    if (values.transmissionInterval) {
      config.transmission_interval = values.transmissionInterval;
      delete values.transmissionInterval;
    }
    if (values.tilt_axis_layout) {
      config.tilt_axis_layout = values.tilt_axis_layout;
      delete values.tilt_axis_layout;
    }
    if (values.tilt_axis_angles) {
      config.tilt_axis_angles = values.tilt_axis_angles;
      delete values.tilt_axis_angles;
    }

    for (const [key, value] of Object.entries(values)) {
      if (ALLOWED_CONFIG_KEYS.includes(key)) {
        config[key] = value;
        delete values[key];
      }
    }
    const apiClientPath = user?.admin
      ? "/admin/station-config"
      : "/organization-member/station-config";
    apiClient
      .request(apiClientPath, {
        method: "POST",
        data: {
          stationId: station.id,
          ...values,
          config,
          configDisplayNames,
        },
      })
      .then(async () => {
        toast.success("Data saved successfully");
        await apolloClient.query({
          query: GET_MEASUREMENT_STATIONS,
          fetchPolicy: "network-only",
        });
        await apolloClient.query({
          query: GET_DASHBOARD_STATIONS,
          variables: {
            slug: station.organization?.slug ?? "",
          },
          fetchPolicy: "network-only",
        });
        refetch()
          .then(() => {
            setEditedValues({});
          })
          .catch(console.error);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Error saving data: " + e.toString());
      });
  };

  const bodyContent = user?.admin ? (
    <EditPageAdminContent
      station={station}
      editedValues={editedValues}
      defaultInterval={defaultInterval}
      config={config}
      configDisplayNames={configDisplayNames}
      onSave={onSave}
      setValueHandler={setValueHandler}
      setDisplayNameHandler={setDisplayNameHandler}
      submitDisabled={submitDisabled}
      setValue={setValue}
      setEditedValues={setEditedValues}
    />
  ) : (
    <EditPageOrganizationMemberContent
      editedValues={editedValues}
      config={config}
      configDisplayNames={configDisplayNames}
      onSave={onSave}
      submitDisabled={submitDisabled}
      setValue={setValue}
      setDisplayNameHandler={setDisplayNameHandler}
    />
  );

  return (
    <div className="MeasurementStationEditPage">
      <BackToLink to={`/stations/${identifier}`}>
        Back to measurement station.
      </BackToLink>
      <PageHeader
        title={gettext(`Edit measurement station`)}
        className="mb-3"
      ></PageHeader>
      <Card className="mb-3">
        <Card.Body>{bodyContent}</Card.Body>
      </Card>
      {!!user?.admin && (
        <Card className="mb-3">
          <Card.Body>
            <CloneStationForm station={station} />
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

interface IntegerInputFieldProps {
  label: string;
  keyName: string;
  displayName: string;
  defaultValue: any;
  setValue: (key: string, value: any) => void;
  setDisplayNameHandler: (key: string) => (e: any) => void;
}

const IntegerInputField = ({
  label,
  keyName,
  displayName,
  defaultValue,
  setValue,
  setDisplayNameHandler,
}: IntegerInputFieldProps) => {
  const { user } = useAuth();
  const placeholder = keyName !== "pnum2" ? "0" : "";
  return (
    <tr>
      <th>
        {user?.admin ? (
          <div>
            <FormControl
              value={displayName || label || ""}
              onChange={setDisplayNameHandler(keyName)}
            />
            {!!displayName && label !== displayName && (
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {label}
              </div>
            )}
          </div>
        ) : (
          displayName || label
        )}
      </th>
      <td>
        <FormControl
          value={defaultValue ? defaultValue : ""}
          placeholder={placeholder}
          onChange={(e) => {
            let val = parseInt(e.target.value);
            if (!isValidDataPointValue(val)) {
              val = 0;
            }
            setValue(keyName, val);
          }}
        />
      </td>
    </tr>
  );
};

interface DynamicParameterBlockProps {
  editedValues: Record<string, any>;
  config: Record<string, any>;
  configDisplayNames: Record<string, string>;
  setValue: (key: string, value: any) => void;
  setDisplayNameHandler: (key: string) => (e: any) => void;
}

const DynamicParameterBlock = ({
  editedValues,
  config,
  configDisplayNames,
  setValue,
  setDisplayNameHandler,
}: DynamicParameterBlockProps) => {
  return (
    <>
      {ALLOWED_CONFIG_KEYS.map((key) => (
        <IntegerInputField
          key={key}
          label={CONFIG_DEFAULT_LABELS[key] || key}
          keyName={key}
          displayName={configDisplayNames[key] || ""}
          defaultValue={editedValues[key] ?? config[key]}
          setValue={setValue}
          setDisplayNameHandler={setDisplayNameHandler}
        />
      ))}
    </>
  );
};

interface DefaultEditPageContentProps {
  editedValues: Record<string, any>;
  config: Record<string, any>;
  configDisplayNames: Record<string, string>;
  onSave: (e: any) => void;
  submitDisabled: boolean;
  setValue: (key: string, value: any) => void;
  setDisplayNameHandler: (key: string) => (e: any) => void;
}

interface EditPageAdminContentProps extends DefaultEditPageContentProps {
  station: GetMeasurementStationEditDetails_station;
  defaultInterval: number;
  setValueHandler: (
    key: string,
    parser?: (raw: string) => any,
  ) => (e: any) => void;
  setEditedValues: (values: Record<string, any>) => void;
}

interface EditPageOrganizationMemberContentProps extends DefaultEditPageContentProps {}

const EditPageAdminContent = ({
  station,
  editedValues,
  defaultInterval,
  config,
  configDisplayNames,
  onSave,
  setValueHandler,
  setDisplayNameHandler,
  submitDisabled,
  setValue,
  setEditedValues,
}: EditPageAdminContentProps) => {
  const measurementInterval =
    editedValues.measurementInterval ?? config.measurement_interval;
  const transmissionInterval =
    editedValues.transmissionInterval ?? config.transmission_interval;
  const { data: organizationList } = useQuery<GetOrganizations>(
    GET_ORGANIZATIONS,
    { variables: {} },
  );
  const { data: sourcesList } = useQuery<
    GetStationSources,
    GetStationSourcesVariables
  >(GET_STATION_SOURCES, {
    variables: {
      stationId: station.id,
    },
  });
  const { data: layerQuery } = useQuery<GetMapLayersQuery>(GET_MAP_LAYERS);
  const sources: GetStationSources_datasource[] = sourcesList?.datasource || [];
  const organizations: GetOrganizations_organization[] =
    organizationList?.organization || [];

  const mapLayers = layerQuery?.map_layer ? [...layerQuery?.map_layer] : [];
  mapLayers.sort((a, b) => (a.id > b.id ? 1 : -1));

  const tiltAxisLayout = getTiltAxisLayout(
    editedValues.tilt_axis_layout ?? config.tilt_axis_layout,
  );
  const tiltAxisAngles = getEffectiveTiltAngles(tiltAxisLayout.id, {
    ...(config.tilt_axis_angles ?? {}),
    ...(editedValues.tilt_axis_angles ?? {}),
  });
  const setTiltAxisAngle = (axisLabel: string, angle: number) => {
    setValue("tilt_axis_angles", { ...tiltAxisAngles, [axisLabel]: angle });
  };
  const setTiltAxisLayout = (layoutId: string) => {
    const layout = getTiltAxisLayout(layoutId);
    setEditedValues({
      ...editedValues,
      tilt_axis_layout: layout.id,
      tilt_axis_angles: { ...layout.defaultAngles },
    });
  };

  return (
    <Table>
      <tbody>
        <tr>
          <th>Identifier</th>
          <td>
            <code>{station.identifier}</code>
          </td>
        </tr>
        <tr>
          <th>Name</th>
          <td>
            <FormControl
              value={editedValues.name ?? station.name ?? ""}
              placeholder={defaultInterval.toString()}
              onChange={setValueHandler("name")}
            />
          </td>
        </tr>
        <tr>
          <th>Organization</th>
          <td>
            <Form.Select
              required
              aria-label="Select an organization"
              onChange={setValueHandler("organization_id")}
              value={
                editedValues?.organization_id ?? station.organization_id ?? ""
              }
            >
              <option value={""}>No organization selected</option>
              {sortByTranslatedKey(organizations, "name").map(
                (organization: GetOrganizations_organization) => (
                  <option
                    key={`org_${organization.id}`}
                    value={organization.id}
                  >
                    {organization.name}
                  </option>
                ),
              )}
            </Form.Select>
          </td>
        </tr>
        <tr>
          <th>Coordinates (lat/lon)</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="number"
                  value={editedValues.lat ?? station.lat ?? ""}
                  placeholder="Latitude"
                  onChange={setValueHandler("lat")}
                />
              </Col>
              <Col>
                <FormattedFormControl
                  type="number"
                  value={editedValues.lon ?? station.lon ?? ""}
                  placeholder="Longitude"
                  onChange={setValueHandler("lon")}
                />
              </Col>
            </Row>
          </td>
        </tr>
        <tr>
          <th>Reference Coordinates (lat/lon)</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="number"
                  value={
                    editedValues.reference_lat ?? station.reference_lat ?? ""
                  }
                  placeholder="Latitude"
                  onChange={setValueHandler("reference_lat")}
                />
              </Col>
              <Col>
                <FormattedFormControl
                  type="number"
                  value={
                    editedValues.reference_lon ?? station.reference_lon ?? ""
                  }
                  placeholder="Longitude"
                  onChange={setValueHandler("reference_lon")}
                />
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    const currentLat = editedValues.lat ?? station.lat;
                    const currentLon = editedValues.lon ?? station.lon;
                    const updates: Record<string, any> = {};

                    if (currentLat !== null && currentLat !== undefined) {
                      updates.reference_lat = currentLat;
                    }
                    if (currentLon !== null && currentLon !== undefined) {
                      updates.reference_lon = currentLon;
                    }

                    setEditedValues({
                      ...editedValues,
                      ...updates,
                    });
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </td>
        </tr>

        <tr>
          <th>Movement distance alert threshold (m)</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="number"
                  value={
                    editedValues.distance_alert_threshold ??
                    station.distance_alert_threshold ??
                    ""
                  }
                  placeholder="Meters"
                  onChange={setValueHandler("distance_alert_threshold")}
                />
              </Col>
            </Row>
          </td>
        </tr>

        <tr>
          <th>Alert recipient emails (comma-separated)</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="nullableString"
                  value={
                    editedValues.alert_recipient_emails ??
                    station.alert_recipient_emails ??
                    ""
                  }
                  placeholder="email@example.com, email2@example.com"
                  onChange={setValueHandler("alert_recipient_emails")}
                />
              </Col>
            </Row>
          </td>
        </tr>

        <tr>
          <th>Map layer</th>
          <td>
            <Form.Select
              required
              aria-label="Select the default map layer"
              onChange={setValueHandler("map_layer")}
              value={editedValues?.map_layer ?? station.map_layer ?? ""}
            >
              {mapLayers.map((layer) => (
                <option key={layer.system_name} value={layer.system_name}>
                  {layer.display_name}
                </option>
              ))}
            </Form.Select>
          </td>
        </tr>

        <tr>
          <th>Primary parameter</th>
          <td>
            <Form.Select
              required
              aria-label="Select the primary parameter for this station"
              onChange={setValueHandler("primary_source_uuid")}
              value={
                editedValues?.primary_source_uuid ??
                station.primary_source_uuid ??
                ""
              }
            >
              <option value={""}>
                Select the primary parameter for this station
              </option>
              {sortByTranslatedKey(sources, "name").map(
                (source: GetStationSources_datasource) => (
                  <option
                    key={`source_${source.source_uuid}`}
                    value={source.source_uuid}
                  >
                    {source.name}
                  </option>
                ),
              )}
            </Form.Select>
          </td>
        </tr>

        <tr>
          <th>Measurement interval (min)</th>
          <td>
            <FormControl
              value={measurementInterval ? measurementInterval / 60 : ""}
              placeholder={defaultInterval.toString()}
              onChange={(e) => {
                let val = parseFloat(e.target.value);
                if (!val) {
                  // also checks NaN
                  val = 0;
                }
                setValue("measurementInterval", val * 60);
              }}
            />
          </td>
        </tr>
        <tr>
          <th>Transmission interval (min)</th>
          <td>
            <FormControl
              value={transmissionInterval ? transmissionInterval / 60 : ""}
              placeholder={defaultInterval.toString()}
              onChange={(e) => {
                let val = parseFloat(e.target.value);
                if (!isValidDataPointValue(val)) {
                  // also checks NaN
                  val = 0;
                }
                setValue("transmissionInterval", val * 60);
              }}
            />
          </td>
        </tr>
        <DynamicParameterBlock
          editedValues={editedValues}
          config={config}
          configDisplayNames={configDisplayNames}
          setValue={setValue}
          setDisplayNameHandler={setDisplayNameHandler}
        />
        <tr>
          <th>Status</th>
          <td>
            <Form.Select
              required
              aria-label="Select station status"
              onChange={setValueHandler("status")}
              value={editedValues?.status ?? station.status ?? ""}
            >
              <option value="active">Online</option>
              <option value="hold">On Hold</option>
              <option value="inactive">Offline</option>
              <option value="samplepoint">Manual Point</option>
            </Form.Select>
          </td>
        </tr>
        <tr>
          <th>Station type</th>
          <td>
            <Form.Select
              aria-label="Select station type"
              onChange={setValueHandler("station_type")}
              value={editedValues?.station_type ?? station.station_type ?? ""}
            >
              <option value="">Not set</option>
              <option value="gnss">GNSS</option>
              <option value="tiltmeter">Tiltmeter</option>
            </Form.Select>
          </td>
        </tr>
        {(editedValues.station_type ?? station.station_type) ===
          "tiltmeter" && (
          <>
            <tr>
              <th>Tilt axis layout</th>
              <td>
                <Form.Select
                  aria-label="Select tilt axis layout"
                  value={tiltAxisLayout.id}
                  onChange={(e) => setTiltAxisLayout(e.target.value)}
                  style={{ maxWidth: "220px" }}
                >
                  {Object.values(TILT_AXIS_LAYOUTS).map((layout) => (
                    <option key={layout.id} value={layout.id}>
                      {gettext(layout.label)}
                    </option>
                  ))}
                </Form.Select>
              </td>
            </tr>
            <tr>
              <th>Tilt axis directions</th>
              <td>
                <Row>
                  {TILT_AXIS_META.map((axis) => (
                    <Col key={axis.label} xs="auto">
                      <div
                        className="mb-1 text-center font-weight-bold"
                        style={{ color: axis.color }}
                      >
                        {axis.label}
                      </div>
                      <Form.Select
                        aria-label={`Direction for ${axis.label} axis`}
                        value={String(tiltAxisAngles[axis.label])}
                        onChange={(e) =>
                          setTiltAxisAngle(axis.label, Number(e.target.value))
                        }
                      >
                        {tiltAxisLayout.directions.map((direction) => (
                          <option key={direction.angle} value={direction.angle}>
                            {gettext(direction.label)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  ))}
                </Row>
              </td>
            </tr>
          </>
        )}
        <tr>
          <th>FTP Config File</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="nullableString"
                  value={
                    editedValues.ftp_config_file ??
                    station.ftp_config_file ??
                    ""
                  }
                  placeholder="ftp://pilvi.mittausguru.fi/90000.conf"
                  onChange={setValueHandler("ftp_config_file")}
                />
              </Col>
            </Row>
          </td>
        </tr>
        <tr>
          <th>FTP Config File Transmitted At</th>
          <td>
            {station.ftp_config_transmitted_at
              ? timestampToDatetimeString(station.ftp_config_transmitted_at)
              : "N/A"}
          </td>
        </tr>
        <tr>
          <th>FTP Firmware File</th>
          <td>
            <Row>
              <Col>
                <FormattedFormControl
                  type="nullableString"
                  value={
                    editedValues.ftp_firmware_file ??
                    station.ftp_firmware_file ??
                    ""
                  }
                  placeholder="ftp://pilvi.mittausguru.fi/prometheus-adn42-4.5.1.ota"
                  onChange={setValueHandler("ftp_firmware_file")}
                />
              </Col>
            </Row>
          </td>
        </tr>
        <tr>
          <th>FTP Firmware File Transmitted At</th>
          <td>
            {station.ftp_firmware_transmitted_at
              ? timestampToDatetimeString(station.ftp_firmware_transmitted_at)
              : "N/A"}
          </td>
        </tr>
        <tr>
          <th>Current Firmware Version</th>
          <td>
            {station.ftp_firmware_version
              ? station.ftp_firmware_version
              : "N/A"}
          </td>
        </tr>
        <tr>
          <td colSpan={2}>
            <Button type="button" onClick={onSave} disabled={submitDisabled}>
              Save
            </Button>
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

const EditPageOrganizationMemberContent = ({
  editedValues,
  config,
  setValue,
  onSave,
  submitDisabled,
  configDisplayNames,
  setDisplayNameHandler,
}: EditPageOrganizationMemberContentProps) => {
  const { user } = useAuth();
  const timeConvert = (minute: number) => {
    let convertedTime = `${minute} min`;
    const numberOfDays = Math.floor(minute / 24 / 60);
    const numberOfHours = Math.floor(minute / 60);
    if ((minute / 60) % 24 === 0 && numberOfDays > 1) {
      convertedTime = `${numberOfDays} d`;
    } else if (minute % 60 === 0) {
      convertedTime = `${numberOfHours} h`;
    }
    return convertedTime;
  };

  const measurementInterval =
    editedValues.measurementInterval ?? config.measurement_interval;
  const transmissionInterval =
    editedValues.transmissionInterval ?? config.transmission_interval;
  const defaultMeasurementIntervalMinutes = [
    1, 5, 10, 15, 30, 60, 120, 360, 720, 1440,
  ];
  const defaultTransmissionIntervalMinutes = [
    5, 15, 30, 60, 180, 360, 720, 1440, 2880, 4320, 10080,
  ];
  return (
    <Table>
      <tbody>
        <tr>
          <th>Measurement interval</th>
          <td>
            <ToggleButtonGroup
              id="measurement-group"
              name="measurement-group"
              type="radio"
              defaultValue={measurementInterval ? measurementInterval / 60 : ""}
              onChange={(value) => {
                let val = parseFloat(value);
                if (!isValidDataPointValue(val)) {
                  val = 0;
                }
                setValue("measurementInterval", val * 60);
              }}
            >
              {defaultMeasurementIntervalMinutes.map((minute, index) => {
                return (
                  <ToggleButton
                    size="sm"
                    key={`MeasurementKey-${index}`}
                    id={`MeasurementInterval-${index}`}
                    variant={"outline-primary"}
                    value={minute}
                  >
                    {timeConvert(minute)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </td>
        </tr>

        <tr>
          <th>Transmission interval</th>
          <td>
            <ToggleButtonGroup
              id="transmission-group"
              name="transmission-group"
              type="radio"
              defaultValue={
                transmissionInterval ? transmissionInterval / 60 : ""
              }
              onChange={(value) => {
                let val = parseFloat(value);
                if (!isValidDataPointValue(val)) {
                  val = 0;
                }
                setValue("transmissionInterval", val * 60);
              }}
            >
              {defaultTransmissionIntervalMinutes.map((minute, index) => {
                return (
                  <ToggleButton
                    size="sm"
                    key={`TransmissionKey-${index}`}
                    id={`Transmission-Interval-${index}`}
                    variant={"outline-primary"}
                    value={minute}
                  >
                    {timeConvert(minute)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </td>
        </tr>
        {user?.canEditStationParameters && (
          <DynamicParameterBlock
            editedValues={editedValues}
            config={config}
            configDisplayNames={configDisplayNames}
            setValue={setValue}
            setDisplayNameHandler={setDisplayNameHandler}
          />
        )}

        <tr>
          <td colSpan={2}>
            <Button type="button" onClick={onSave} disabled={submitDisabled}>
              Save
            </Button>
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

interface CloneStationFormProps {
  station: GetMeasurementStationEditDetails_station;
}
const CloneStationForm = (props: CloneStationFormProps) => {
  const { station } = props;
  const [newName, setNewName] = React.useState("");
  const [newIdentifier, setNewIdentifier] = React.useState("");
  const [cloneFormulas, setCloneFormulas] = React.useState(false);
  const [cloneAnnotations, setCloneAnnotations] = React.useState(false);
  const [cloneData, setCloneData] = React.useState(false);
  const [cloneManualData, setCloneManualData] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const navigate = useNavigate();
  const submitDisabled = !newName || !newIdentifier || submitting;

  const onSave = (e: any) => {
    e.preventDefault();
    if (submitDisabled || !station.identifier) {
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    const answer = confirm(
      `Clone station ${station.name} [${station.identifier}] as ${newName} [${newIdentifier}]?`,
    );
    if (!answer) {
      return;
    }
    setSubmitting(true);
    const apiClientPath = "/admin/clone-station";
    apiClient
      .request(apiClientPath, {
        method: "POST",
        data: {
          identifier: station.identifier,
          newIdentifier,
          newName,
          cloneFormulas,
          cloneAnnotations,
          cloneData,
          cloneManualData,
        },
      })
      .then((data) => {
        setSubmitting(false);
        toast.success("Station cloned successfully");
        const newIdentifier = data?.newStation?.identifier;
        if (newIdentifier) {
          navigate(`/stations/${newIdentifier}`);
        }
      })
      .catch((e) => {
        setSubmitting(false);
        console.error(e);
        toast.error("Error cloning station data: " + e.toString());
      });
  };

  return (
    <div>
      <h2>Clone station</h2>
      <Table>
        <tbody>
          <tr>
            <th>New identifier</th>
            <td>
              <FormControl
                value={newIdentifier}
                onChange={(e) => {
                  setNewIdentifier(e.target.value);
                }}
              />
            </td>
          </tr>
          <tr>
            <th>New name</th>
            <td>
              <FormControl
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                }}
              />
            </td>
          </tr>
          <tr>
            <th>Clone formulas?</th>
            <td>
              <Form.Check
                checked={cloneFormulas}
                onChange={(e) => {
                  setCloneFormulas(e.target.checked);
                }}
              />
            </td>
          </tr>
          <tr>
            <th>Clone notes?</th>
            <td>
              <Form.Check
                checked={cloneAnnotations}
                onChange={(e) => {
                  setCloneAnnotations(e.target.checked);
                }}
              />
            </td>
          </tr>
          <tr>
            <th>Clone data?</th>
            <td>
              <Form.Check
                checked={cloneData}
                onChange={(e) => {
                  setCloneData(e.target.checked);
                }}
              />
            </td>
          </tr>
          <tr>
            <th>Clone manual/lab data?</th>
            <td>
              <Form.Check
                checked={cloneManualData}
                onChange={(e) => {
                  setCloneManualData(e.target.checked);
                }}
              />
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <Button type="button" onClick={onSave} disabled={submitDisabled}>
                Clone
              </Button>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

export default MeasurementStationEditPage;
